// Dashboard template registry and matching logic

export interface DashboardTemplate {
  id: string
  name: string
  description: string
  regions: string[]
  useCases: string[]
  viewComponent: string // Component name to render
}

export const dashboardTemplates: DashboardTemplate[] = [
  {
    id: 'region-pricing',
    name: 'Regional Pricing Dashboard',
    description: 'Power pricing analysis with trading opportunities',
    regions: [
      'germany',
      'spain',
      'france',
      'uk',
      'italy',
      'nordics',
      'netherlands',
      'belgium',
    ],
    useCases: ['pricing', 'price', 'market', 'trading', 'opportunities'],
    viewComponent: 'RegionPricingDashboard',
  },
  {
    id: 'region-weather',
    name: 'Regional Weather Dashboard',
    description: 'Weather forecasts and renewable generation',
    regions: [
      'germany',
      'spain',
      'france',
      'uk',
      'italy',
      'nordics',
      'netherlands',
      'belgium',
    ],
    useCases: [
      'weather',
      'wind',
      'solar',
      'forecast',
      'renewable',
      'generation',
    ],
    viewComponent: 'RegionWeatherDashboard',
  },
  {
    id: 'region-trading',
    name: 'Regional Trading Dashboard',
    description: 'Trading strategies and execution opportunities',
    regions: [
      'germany',
      'spain',
      'france',
      'uk',
      'italy',
      'nordics',
      'netherlands',
      'belgium',
    ],
    useCases: [
      'trading',
      'strategy',
      'execution',
      'opportunities',
      'positions',
    ],
    viewComponent: 'RegionTradingDashboard',
  },
  {
    id: 'europe-overview',
    name: 'Europe Overview Dashboard',
    description: 'Pan-European market overview and analysis',
    regions: ['europe', 'eu', 'european'],
    useCases: ['overview', 'market', 'pricing', 'analysis'],
    viewComponent: 'EuropePricingDashboard',
  },
]

export interface DashboardConfig {
  templateId: string
  region: string
  customization: {
    title: string
    description: string
    focusAreas?: string[]
  }
}

/**
 * Parse user prompt and match to appropriate dashboard template
 */
export function matchDashboardTemplate(prompt: string): DashboardConfig | null {
  const lowerPrompt = prompt.toLowerCase()

  // Extract region
  let detectedRegion = 'europe'
  for (const template of dashboardTemplates) {
    for (const region of template.regions) {
      if (lowerPrompt.includes(region)) {
        detectedRegion = region
        break
      }
    }
    if (detectedRegion !== 'europe') break
  }

  // Extract use case and match template
  let matchedTemplate: DashboardTemplate | null = null
  let maxScore = 0

  for (const template of dashboardTemplates) {
    let score = 0

    // Check if region matches
    if (template.regions.includes(detectedRegion)) {
      score += 2
    }

    // Check use case keywords
    for (const useCase of template.useCases) {
      if (lowerPrompt.includes(useCase)) {
        score += 1
      }
    }

    if (score > maxScore) {
      maxScore = score
      matchedTemplate = template
    }
  }

  if (!matchedTemplate) {
    // Default to pricing dashboard
    matchedTemplate =
      dashboardTemplates.find((t) => t.id === 'region-pricing') ||
      dashboardTemplates[0]
  }

  // Generate title and description
  const regionName =
    detectedRegion.charAt(0).toUpperCase() + detectedRegion.slice(1)
  const title = `${regionName} ${matchedTemplate.name.replace('Regional ', '').replace(' Dashboard', '')}`
  const description = `${matchedTemplate.description} for ${regionName}`

  return {
    templateId: matchedTemplate.id,
    region: detectedRegion,
    customization: {
      title,
      description,
    },
  }
}

/**
 * Generate dashboard ID from config
 */
export function generateDashboardId(config: DashboardConfig): string {
  return `${config.region}-${config.templateId}-${Date.now()}`
}
